/**
 * Graceful Creations — rolling filmstrip of recent work.
 *
 * The motion itself is CSS: the track carries a linear, infinite
 * translate3d from 0 to -50%, and hover and :focus-within pause it. That
 * means the strip holds still for a mouse or a keyboard whether or not this
 * script ever runs.
 *
 * What the script is for is the half. A -50% translation only looks
 * seamless if the track holds the run of frames twice, and Liquid can
 * render the block's children just once, so the duplicate is cloned here.
 * The clones are aria-hidden and taken out of the tab order: they are the
 * same photos and the same links a second time, and a screen reader or a
 * Tab key finding each piece twice would be a bug, not a feature.
 *
 * Duration is set from the block's seconds-per-photo setting multiplied by
 * the number of real frames, so adding a photo lengthens the loop instead
 * of speeding the strip up.
 *
 * Implemented as a custom element so each instance wires itself up on
 * connect. The theme editor tears down and re-renders blocks on every
 * settings change, so a one-shot DOMContentLoaded listener would go dead
 * after the first edit — and the clones have to be rebuilt each time, which
 * is why anything previously cloned is cleared first.
 */

class GcRecentWorks extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gc-works]');
    if (!this.track) return;

    this.build();
  }

  disconnectedCallback() {
    this.clearClones();
  }

  /** Removes any run this script added, leaving the Liquid-rendered frames. */
  clearClones() {
    if (!this.track) return;
    for (const clone of this.track.querySelectorAll('[data-gc-clone]')) {
      clone.remove();
    }
    this.track.removeAttribute('data-gc-rolling');
  }

  build() {
    this.clearClones();

    const originals = Array.from(this.track.querySelectorAll(':scope > .gc-work'));

    // One frame cannot roll against itself: -50% of a two-frame track is
    // the same frame again, which reads as a stutter rather than a strip.
    if (originals.length < 2) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const seconds = Math.max(1, Number(this.track.dataset.interval) || 2);

    for (const frame of originals) {
      const clone = frame.cloneNode(true);
      clone.setAttribute('data-gc-clone', '');
      clone.setAttribute('aria-hidden', 'true');

      // A cloned link is still focusable, so Tab would land on every piece
      // twice and the strip would appear to loop the keyboard back on
      // itself. inert would do this in one attribute; tabindex -1 on the
      // anchors is the version that works everywhere this theme runs.
      for (const link of clone.querySelectorAll('a')) {
        link.setAttribute('tabindex', '-1');
      }

      // Shopify's editor targets blocks by these attributes. Leaving them on
      // a copy gives two elements claiming one block id, and clicking the
      // section in the editor can then scroll to or outline the wrong one.
      clone.removeAttribute('id');
      for (const attr of Array.from(clone.attributes)) {
        if (attr.name.startsWith('data-shopify')) clone.removeAttribute(attr.name);
      }

      this.track.appendChild(clone);
    }

    this.track.style.setProperty('--gc-works-total', seconds * originals.length + 's');
    this.track.setAttribute('data-gc-rolling', '');
  }
}

if (!customElements.get('gc-recent-works-component')) {
  customElements.define('gc-recent-works-component', GcRecentWorks);
}
